<template>
  <div>
    <Header
      :model="appStore.configuration?.components?.header"
      :languages="appStore.configuration?.languages"
      :key="componentKey"
      @open-help="toggleHelpSection"
    />
    <router-view
      @login-successful="onSuccessfulLogin"
      @open-feedback-modal="toggleFeedbackSection($event)"
      @copy-agent-response=""
    />
    <ToastContainer />

    <Footer
      v-if="!chatStore.isFirstQuestionSent || appStore.currentState === State.Login"
      :model="appStore.configuration?.components?.footer"
      :key="componentKey"
    />

    <SupportRequestModal
      :model="appStore.configuration?.components?.supportRequest"
      :key="componentKey"
      @close="closeSupportRequestModal"
      :is-modal-open="isSupportRequestModalOpen"
    ></SupportRequestModal>
    <HelpModal
      :model="appStore.configuration?.components.header.helpModal"
      :key="componentKey"
      :isModalOpen="isHelpModalOpen"
      @close="toggleHelpSection"
    ></HelpModal>
    <FeedbackModal
      :model="appStore.configuration?.components.header.feedbackModal"
      :key="componentKey"
      :isModalOpen="isFeedbackModalOpen"
      @close="toggleFeedbackSection(0)"
      :grade="feedbackGrade"
    ></FeedbackModal>
    <SessionExpiredModal
      :model="appStore.configuration?.components.sessionExpiredModal"
      :key="componentKey"
      :isModalOpen="isAidaSessionExpired"
      @close="closeSessionExpiredModal"
    >
    </SessionExpiredModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { ref } from 'vue'
import { useAppStore } from './stores/appStore'
import { useUserProfileStore } from './stores/userProfileStore'
import { State } from './types/app/state'
import Header from './components/Header.vue'
import Footer from './components/Footer.vue'
import SupportRequestModal from './components/supportRequest/SupportRequestModal.vue'
import HelpModal from './components/HelpModal.vue'
import FeedbackModal from './components/FeedbackModal.vue'
import { RouterView } from 'vue-router'
import router from './router'
import { SupportRequestState } from './types/app/supportRequestState'
import { LoginRequired } from './types/app/loginRequired'
import { useChatStore } from './stores/chatStore'
import type { FeedbackGrade } from './types/FeedbackGrade'
import { useAidaSessionStore } from './stores/aidaSessionStore'
import { storeToRefs } from 'pinia'
import SessionExpiredModal from './components/SessionExpiredModal.vue'
import ToastContainer from './components/ToastContainer.vue'
import { useRoute } from 'vue-router'

let isHelpModalOpen = ref(false)
let isFeedbackModalOpen = ref(false)
let isSupportRequestModalOpen = ref(false)

let componentKey = ref(0)
let feedbackGrade = ref<FeedbackGrade | null>(null)

const appStore = useAppStore()
const userProfileStore = useUserProfileStore()
const chatStore = useChatStore()
const aidaSessionStore = useAidaSessionStore()
const route = useRoute()

const { isAidaSessionExpired } = storeToRefs(aidaSessionStore)

function toggleHelpSection() {
  isHelpModalOpen.value = !isHelpModalOpen.value
}

function toggleFeedbackSection(grade: FeedbackGrade) {
  feedbackGrade.value = grade
  isFeedbackModalOpen.value = !isFeedbackModalOpen.value
}

function closeSessionExpiredModal() {
  aidaSessionStore.setAidaSessionExpired(false)
}

onMounted(async () => {
  if (appStore.configuration) {
    componentKey.value += 1

    const isOAuthCallbackRoute = route.name === 'oauthCallback'

    if (!isOAuthCallbackRoute) {
      // This should only run on a "fresh" load, not when coming back from IdP
      userProfileStore.setIsLoggedIn(false)

      if (appStore.configuration?.login.isLoginRequired === LoginRequired.Always) {
        appStore.setState(State.Login)
      } else {
        appStore.setState(State.Main)
      }
    }
  }
})

const currentState = computed(() => appStore.currentState)
const currentSupportRequestState = computed(() => appStore.currentSupportRequestState)
const isLoggedIn = computed(() => userProfileStore.isLoggedIn)
const authRouteNames = new Set(['login', 'oauthCallback', 'loginFailed'])

watch(
  () => ({
    currentApplicationState: currentState.value,
    currentSupportRequestState: currentSupportRequestState.value,
    userIsLoggedIn: !!isLoggedIn.value,
    loginRequirement: appStore.configuration?.login.isLoginRequired,
    currentRouteName: router.currentRoute.value?.name as string | undefined,
  }),
  async ({
    currentApplicationState,
    currentSupportRequestState,
    userIsLoggedIn,
    loginRequirement,
    currentRouteName,
  }) => {
    // Support Request flow (applies when user tries to open SR)
    if (currentApplicationState === State.AttemptToOpenSR) {
      // Disabled - open immediately
      if (loginRequirement === LoginRequired.Never) {
        openSupportRequestModal()
        appStore.setState(State.Main)
        return
      }

      // 'srRequiredOnly' or 'always' - require login first
      if (!userIsLoggedIn) {
        const isOnAuthRoute = currentRouteName && authRouteNames.has(currentRouteName)
        if (!isOnAuthRoute) {
          await router.push({ name: 'login' })
        }
        return
      }

      // Logged in after intent - open SR
      openSupportRequestModal()
      appStore.setState(State.Main)
      return
    }

    // When SR state is open - flip only the modal boolean
    if (
      currentSupportRequestState === SupportRequestState.Open &&
      !isSupportRequestModalOpen.value
    ) {
      openSupportRequestModal()
      appStore.setState(State.Main)
      return
    }
  },
  { immediate: true }
)

function closeSupportRequestModal() {
  isSupportRequestModalOpen.value = false
  if (currentSupportRequestState.value === SupportRequestState.Open)
    appStore.setSupportRequestState(SupportRequestState.Closed)
}

function openSupportRequestModal() {
  isSupportRequestModalOpen.value = true
  if (currentSupportRequestState.value === SupportRequestState.Closed)
    appStore.setSupportRequestState(SupportRequestState.Open)
}

function onSuccessfulLogin() {
  if (currentState.value != State.AttemptToOpenSR) {
    appStore.setState(State.Main)
  }
}
</script>

<style scoped lang="scss"></style>
